import type { ApiClient, Schemas } from '@argzombie/api-client';
import { ok } from '../api';

/*
 * The health intake admin calls (health intake CONTRACT §3, `AdminHealthEndpoints`), read-only, through the typed client.
 *
 * | Function | Endpoint |
 * |---|---|
 * | `playerHealth` | `GET /admin/players/{id}/health` (links, activity profile, workout ledger newest 200) |
 * | `healthSources` | `GET /admin/health/sources` (every app that wrote workouts, per platform; tier null = not trusted) |
 */

export type PlayerHealth = Schemas['AdminPlayerHealthResponse'];
export type HealthLink = Schemas['HealthLinkDto'];
export type ActivityProfile = Schemas['ActivityProfileDto'];
export type DayActivity = Schemas['DayActivityDto'];
export type WorkoutRow = Schemas['WorkoutRowDto'];
export type HealthSourceRow = Schemas['AdminHealthSourceRow'];

export const playerHealth = async (client: ApiClient, playerId: string) =>
  ok(await client.GET('/admin/players/{id}/health', { params: { path: { id: playerId } } }));

export const healthSources = async (client: ApiClient) =>
  ok(await client.GET('/admin/health/sources'));

/** Shared-cs `HealthPlatforms` with their store names. */
export const platformLabel = (p: string | null | undefined) =>
  p === 'ios' ? 'Apple Health' : p === 'android' ? 'Health Connect' : (p ?? '');

/** Unix ms as a local date-time, or '—' for 0/none (the API sends 0 for "never"). */
export const msWhen = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString() : '—');

/** A number rounded to `digits` decimals, or '—' for null. */
export const fmt = (v: number | null | undefined, digits = 0) =>
  v == null || !Number.isFinite(v) ? '—' : v.toFixed(digits);

/** Metres as km with one decimal, or '—'. */
export const km = (m: number | null | undefined) =>
  m == null ? '—' : `${(m / 1000).toFixed(1)} km`;

/** The colour chip class for a tier 0..3 (CONTRACT §4): 3 green, 2 blue, 1 yellow, 0 red. Palette tokens only (ADR 0051). */
export function tierChipClass(tier: number | null | undefined): string {
  switch (tier) {
    case 3:
      return 'bg-formaldehyde text-ink';
    case 2:
      return 'bg-benday text-ink';
    case 1:
      return 'bg-caption text-ink';
    default:
      return 'bg-blam text-white';
  }
}

/** The player-local day of a row: its start shifted by its own tz offset (the API's day_local rule). */
export function rowDay(r: WorkoutRow): string {
  if (!r.startMs) return '';
  return new Date(r.startMs + (r.tzOffsetMin ?? 0) * 60_000).toISOString().slice(0, 10);
}

/** The ledger newest first (by start, then id), whatever order the API sent. */
export const newestFirst = (rows: readonly WorkoutRow[]) =>
  [...rows].sort((a, b) => (b.startMs ?? 0) - (a.startMs ?? 0) || (b.id ?? 0) - (a.id ?? 0));

/** "not in trustedSources" for a sources row the server could not tier. */
export const isUntrusted = (s: HealthSourceRow) => s.tier == null;
